import { Rank, ShPhase, Suit, type Card, type ShTableState } from "../gen/bazivan/v1/table_pb";
import { placeLabel } from "../game/place";
import { fa, sortHand } from "../game/cards";
import type { SeatTag } from "../hokm/scene";
import { toLocal, toServer } from "../hokm/state";

export { toLocal, toServer };
export { ShPhase };

/**
 * وضعیتِ محلیِ میزِ شلم.
 *
 * همان دو کارِ حکم: چرخشِ صندلی تا خودم صندلیِ ۰ باشم، و دستِ حریف فقط به
 * تعداد. آنچه شلم اضافه دارد مزایده است — و مزایده هم صندلی‌دار است، پس
 * `bidTurn`, `passed` و `highSeat` هم محلی می‌شوند. اگر یکی‌شان سرورمطلق
 * بماند، «پاس» روی صندلیِ اشتباه روشن می‌شود و بازیکن نمی‌فهمد نوبتِ کیست.
 */
export type ShelemLocal = {
  n: number;
  me: number;
  phase: ShPhase;

  myHand: Card[];
  counts: number[];            // شمارِ کارتِ هر صندلیِ محلی

  // مزایده — همه محلی
  dealer: number;
  bidTurn: number;
  passed: boolean[];
  highBid: number;
  highSeat: number;            // ‎-۱ = هنوز کسی نخوانده
  bidFloor: number;
  canRaise: boolean;
  mustBid: boolean;            // خودم شروع‌کننده‌ام و پاس ندارم

  declarer: number;            // محلی؛ ‎-۱ تا پایانِ مزایده
  bid: number;
  trump: Suit;
  trumpChosen: boolean;

  trick: (Card | null)[];      // کارتِ زمینِ هر صندلیِ محلی
  trickLeader: number;
  ledSuit: Suit;
  trickHasLead: boolean;
  turn: number;

  tricksWon: number[];         // به واحدِ محلی
  collected: number[];         // امتیازِ جمع‌شده‌ی هر واحدِ محلی (جمعشان ۱۶۵)
  score: number[];             // به واحدِ محلی؛ منفی هم می‌شود
  targetScore: number;
  handOver: boolean;
  matchOver: boolean;
  myUnit: number;

  names: string[];
  /** شناسه‌ی بازیکنِ هر صندلی (ربات خالی) — حلقه‌ی صدای زنده. */
  playerIds: string[];
  bots: boolean[];
  avatars: string[];
  connected: boolean[];
  subscribers: boolean[];
  places: string[];
  countries: string[];
  carpet: string;

  // «حالا از من چه می‌خواهند؟» — کلاینت از روی همین تصمیم می‌گیرد چه نشان بدهد
  myTurn: boolean;             // نوبتِ کارت‌انداختنِ من
  awaitingBidFromMe: boolean;
  awaitingDiscardFromMe: boolean;
  awaitingTrumpFromMe: boolean;

  turnRemaining: number;       // ثانیه
  /** نمای تماشاچی: هیچ دستی مالِ من نیست — [[HokmLocal.watching]]. */
  watching: boolean;
};

export const UNITS = 2;
export const unitOf = (seat: number) => seat % UNITS;
export const toLocalUnit = (serverUnit: number, me: number) =>
  ((serverUnit - (me % UNITS)) % UNITS + UNITS) % UNITS;

export const sameCard = (a: Card, b: Card) => a.suit === b.suit && a.rank === b.rank;

export function emptyShelem(n = 4): ShelemLocal {
  return {
    n, me: 0, phase: ShPhase.UNSPECIFIED,
    myHand: [], counts: new Array(n).fill(0),
    dealer: 0, bidTurn: 0, passed: new Array(n).fill(false),
    highBid: 0, highSeat: -1, bidFloor: 100, canRaise: true, mustBid: false,
    declarer: -1, bid: 0, trump: Suit.UNSPECIFIED, trumpChosen: false,
    trick: new Array(n).fill(null), trickLeader: -1, ledSuit: Suit.UNSPECIFIED,
    trickHasLead: false, turn: -1,
    tricksWon: [0, 0], collected: [0, 0], score: [0, 0], targetScore: 0,
    handOver: false, matchOver: false, myUnit: 0,
    names: new Array(n).fill(""), playerIds: new Array(n).fill(""), bots: new Array(n).fill(false),
    avatars: new Array(n).fill(""), connected: new Array(n).fill(true),
    subscribers: new Array(n).fill(false), places: new Array(n).fill(""), countries: new Array(n).fill(""), carpet: "",
    myTurn: false, awaitingBidFromMe: false, awaitingDiscardFromMe: false,
    awaitingTrumpFromMe: false, turnRemaining: 0, watching: false,
  };
}

export function applyShelem(ts: ShTableState): ShelemLocal {
  const n = ts.seats.length > 0 ? ts.seats.length : 4;
  const me = ts.me;
  const st = emptyShelem(n);
  st.me = me;
  st.phase = ts.phase;
  st.watching = ts.watching;
  st.carpet = ts.carpet ?? "";

  const arr = <T,>(v: T) => new Array<T>(n).fill(v);
  st.names = arr(""); st.bots = arr(false); st.avatars = arr("");
  st.connected = arr(true); st.subscribers = arr(false); st.places = arr(""); st.countries = arr("");
  st.playerIds = arr("");
  for (const s of ts.seats) {
    const l = toLocal(s.index, me, n);
    if (l < 0 || l >= n) continue;
    st.names[l] = s.name;
    st.playerIds[l] = s.bot ? "" : s.playerId ?? "";
    st.bots[l] = s.bot;
    st.avatars[l] = s.avatar ?? "";
    st.connected[l] = s.connected || s.bot;
    st.subscribers[l] = !!s.subscriber;
    st.places[l] = placeLabel(s);
    st.countries[l] = s.country ?? "";
  }

  // دستِ مرتب، مثلِ بقیه‌ی میزها — [[applyCb]].
  st.myHand = sortHand(ts.myHand);
  st.counts = arr(0);
  st.trick = arr<Card | null>(null);
  st.passed = arr(false);
  for (let server = 0; server < n; server++) {
    const l = toLocal(server, me, n);
    st.counts[l] = l === 0 && !st.watching ? st.myHand.length : (ts.handCounts[server] ?? 0);
    st.passed[l] = ts.passed[server] ?? false;
    // خانه‌ی خالیِ Card از «هنوز نزده» جدا نمی‌شود، پس بیتِ جدا را می‌خوانیم.
    if (ts.trickPlayed[server]) st.trick[l] = ts.trick[server] ?? null;
  }

  st.dealer = toLocal(ts.dealer, me, n);
  st.bidTurn = toLocal(ts.bidTurn, me, n);
  st.highBid = ts.highBid;
  st.highSeat = ts.highSeat < 0 ? -1 : toLocal(ts.highSeat, me, n);
  st.bidFloor = ts.bidFloor;
  st.canRaise = ts.canRaise;
  st.mustBid = ts.mustBid;

  st.declarer = ts.declarer < 0 ? -1 : toLocal(ts.declarer, me, n);
  st.bid = ts.bid;
  st.trumpChosen = ts.trumpChosen;
  // خالِ پیش از اعلام معنا ندارد — سرور هم UNSPECIFIED می‌فرستد؛ این‌جا صافش
  // می‌کنیم تا هیچ خواننده‌ای مجبور نباشد trumpChosen را هم بپرسد.
  st.trump = ts.trumpChosen ? ts.trump : Suit.UNSPECIFIED;

  st.trickLeader = ts.trickHasLead ? toLocal(ts.trickLeader, me, n) : -1;
  st.ledSuit = ts.trickHasLead ? ts.ledSuit : Suit.UNSPECIFIED;
  st.trickHasLead = ts.trickHasLead;
  st.turn = ts.phase === ShPhase.PLAY ? toLocal(ts.turn, me, n) : -1;

  st.myUnit = unitOf(0); // خودم همیشه صندلیِ محلیِ ۰ام
  for (let u = 0; u < UNITS; u++) {
    const server = toServer(u, me, UNITS);
    st.tricksWon[u] = ts.tricksWon[server] ?? 0;
    st.collected[u] = ts.collected[server] ?? 0;
    st.score[u] = ts.score[server] ?? 0;
  }
  st.targetScore = ts.targetScore;
  st.handOver = ts.handOver;
  st.matchOver = ts.matchOver;

  const live = !st.watching && !ts.handOver && !ts.matchOver;
  st.awaitingBidFromMe = live && ts.phase === ShPhase.BID && st.bidTurn === 0;
  st.awaitingDiscardFromMe = live && ts.phase === ShPhase.DISCARD && st.declarer === 0;
  st.awaitingTrumpFromMe = live && ts.phase === ShPhase.TRUMP && st.declarer === 0;
  st.myTurn = live && ts.phase === ShPhase.PLAY && st.turn === 0;

  st.turnRemaining = ts.turnDeadlineMs / 1000;
  return st;
}

/**
 * قانونِ همخال — همان CanPlayِ سرور.
 *
 * کلاینت قاعده را دوباره نمی‌نویسد تا «حقیقت» دو جا باشد؛ این فقط برای
 * کم‌رنگ‌کردنِ کارتِ نشدنی است. حرفِ آخر همیشه پاسخِ سرور است.
 */
export function canPlay(st: ShelemLocal, c: Card): boolean {
  if (!st.trickHasLead || c.suit === st.ledSuit) return true;
  return !st.myHand.some((h) => h.suit === st.ledSuit);
}

/** آنچه میز همین حالا از من می‌پرسد — هر کدام پنجره‌ی خودش را وسطِ میز دارد. */
export type ShAsk = "bid" | "discard" | "trump" | null;

export function askOf(st: ShelemLocal): ShAsk {
  if (st.awaitingBidFromMe) return "bid";
  if (st.awaitingDiscardFromMe) return "discard";
  if (st.awaitingTrumpFromMe) return "trump";
  return null;
}

/** بیشترین خواندن — همان MaxBid ِ سرور. */
export const MAX_BID = 165;
/** امتیازِ هر دستِ برده، جدا از برگ‌هایش — همان TrickBonus ِ سرور. */
export const TRICK_BONUS = 5;
/** شمارِ برگ‌های زمین — WidowSize ِ سرور. */
export const WIDOW = 4;

/** ارزشِ یک دستِ برده: امتیازِ برگ‌هایش به‌علاوه‌ی پنج — همان Collected ِ سرور، دست‌به‌دست. */
export function trickPoints(trick: readonly (Card | null)[]): number {
  return trick.reduce((a, c) => a + (c ? cardPoints(c) : 0), TRICK_BONUS);
}

/** امتیازِ یک برگ — همان CardPoints ِ سرور: آس و ده ۱۰، پنج ۵. */
export function cardPoints(c: Card): number {
  return c.rank === Rank.ACE || c.rank === Rank.TEN ? 10 : c.rank === Rank.FIVE ? 5 : 0;
}

/**
 * برچسبِ مزایده‌ی هر صندلیِ محلی: «پاس»، بالاترین خواندن، یا خواندنِ آخرش اگر از او بالاتر رفته‌اند.
 *
 * `said` خواندنِ آخرِ هر صندلی است از رویدادهای همین دست — وضعیتِ سرور فقط بالاترین و پاس‌ها
 * را دارد، پس بعد از برگشت از قطعی، خواندنِ کهنه‌ی بقیه نیست و برچسبشان خالی می‌ماند.
 */
export function bidTags(st: ShelemLocal, said: readonly (number | null | undefined)[]): SeatTag[] {
  return Array.from({ length: st.n }, (_, l): SeatTag => {
    if (st.passed[l]) return { text: "پاس", kind: "pass" };
    if (l === st.highSeat && st.highBid > 0) return { text: fa(st.highBid), kind: "high" };
    const v = said[l];
    return v ? { text: fa(v), kind: "bid" } : null;
  });
}
